import {ReportPopupContainer} from "@/utils/presenters/interfaces.ts";
import {AxiosRequestConfig} from "axios";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import NamaURLS from "@/utils/constants/NamaURLS.ts";
import ReportLauncherPlace from "@/utils/presenters/ReportLauncherPlace.ts";
import DomainBaseEntities from "@/utils/constants/DomainBaseEntities.ts";
import ReportViewerPlace from "@/utils/presenters/ReportViewerPlace.ts";
import router from "@/router";
import RouteNames from "@/router/RouteNames.ts";
import PrintingOption from "@/utils/constants/PrintingOption.ts";
import DownloadPurpose from "@/utils/constants/DownloadPurpose.ts";
import {QuestionFieldsSerializer} from "@/utils/urlUtils/CriteriaAndQuestionsURLParser.ts";
import ReportDimensionParametersOverrider from "@/utils/presenters/ReportDimensionParametersOverrider.ts";
import {QuestionFieldsPresenter} from "@/utils/presenters/QuestionFieldsPresenter.ts";
import EmbeddedViewUtils from "@/utils/editView/editScreens/EmbeddedViewUtils.ts";
import AbsPresenter from "@/utils/common/AbsPresenter.ts";
import GUIUtilities from "@/utils/common/GUIUtilities.ts";
import ReportType from "@/utils/constants/ReportType.ts";
import ReportViewerPresenter from "@/utils/presenters/ReportViewerPresenter.ts";
import OutputFormat from "@/utils/constants/OutputFormat.ts";
import ClientCache from "@/utils/login/ClientCache.ts";
import LoginUtils from "@/utils/login/LoginUtils.ts";
import QuestionField = namaTypes.QuestionField;
import ReportMetadata = namaTypes.ReportMetadata;
import EntityReferenceData = namaTypes.EntityReferenceData;
import ReportMetadataContainer = namaTypes.ReportMetadataContainer;
import ByIdRequest = namaTypes.ByIdRequest;
import DTOFilledQuestionField = namaTypes.DTOFilledQuestionField;
import FieldActionContextInfo = namaTypes.FieldActionContextInfo;

export default class ReportLauncherPresenter extends AbsPresenter
{
  static readonly SUPPORTED_FORMATS: string[] = [OutputFormat.HTML, OutputFormat.PDF, OutputFormat.XLSX];

  reportMetadata?: ReportMetadata;
  reportRef?: EntityReferenceData;
  popupContainer?: ReportPopupContainer;
  metadataContainer?: ReportMetadataContainer;
  questionFieldsEditor: QuestionFieldsPresenter = new QuestionFieldsPresenter('');

  constructor(place: ReportLauncherPlace, popupContainer?: ReportPopupContainer) {
    super(place)
    this.popupContainer = popupContainer;
  }

  override fetchPlace(): ReportLauncherPlace {
    return this.place as ReportLauncherPlace;
  }

  async start() {
    if (!this.fetchPlace().reportId) {
      //TODO:: Push Error;
      return undefined;
    }
    let request: ByIdRequest = {
      entityType: DomainBaseEntities.ReportDefinition,
      id: this.fetchPlace().reportId
    };
    let settings: AxiosRequestConfig = {
      data: request
    };
    let response = await NamaCommon.namaFetch(NamaURLS.FetchReportMetadata, settings);
    if (response && response.result && response.result.succeded) {
      this.reportRef = response.data.report;
      this.metadataContainer = response.data;
      await this.init(response.data.metadata);
    }
    this.updateWindowTitle();
  }

  updateWindowTitle() {
    if (!this.popupContainer)
      GUIUtilities.updateWindowTitle(this.place?.getTitleForWindow() ?? '');
  }

  async init(reportMetadata: ReportMetadata) {
    this.reportMetadata = reportMetadata;
    if (this.noQuestionsToProvide()) {
      await this.showReportResult(this.createReportResultPlace(""));
      return;
    }
    await this.updateQuestionFieldsEditorFromMetadata();
  }

  noQuestionsToProvide() {
    let reportMetadata = this.reportMetadata;
    if(!reportMetadata)
      return false;
    return !reportMetadata.questionFields || reportMetadata.questionFields.length == this.getHiddenQuestions().length;
  }

  private async updateQuestionFieldsEditorFromMetadata() {
    this.questionFieldsEditor.ownerId = this.getReportId();
    this.questionFieldsEditor.reportMetadata = this.reportMetadata;
    this.questionFieldsEditor.reportRef = this.reportRef;
    let questionFields: QuestionField[] = this.reportMetadata && this.reportMetadata.questionFields ? this.reportMetadata.questionFields : [];
    await this.questionFieldsEditor.edit(questionFields, this.getDefaultParameterValues(), this.getHiddenQuestions());
  }

  calcDefaultOutputFormat(): string {
    let candidates: (string | undefined)[] = [
      this.fetchPlace().format,
      this.reportMetadata?.viewingFormat,
      LoginUtils.loginInfo?.userInfo?.settings?.viewingFormat,
      ClientCache.getCustomGUISettings()?.tooltipsAndReportsAndRevise?.globalConfigReportsViewingFormat
    ];
    return candidates.find(l => ReportLauncherPresenter.SUPPORTED_FORMATS.contains(l ?? '')) ?? OutputFormat.HTML;
  }

  isDashboardType(): boolean {
    return this.reportRef?.extraData?.reportType == ReportType.Dashboard
  }

  isDownloadFormat(format: string): boolean {
    return !this.isDashboardType() && format == OutputFormat.XLSX;
  }

  async onRunClick(inNewWindow: boolean, format: string) {
    let reportResultPlace: ReportViewerPlace = this.createReportResultPlace();
    this.fetchPlace().reportId = reportResultPlace.reportId;
    this.fetchPlace().questions = reportResultPlace.questions;
    this.fetchPlace().tempPrintId = reportResultPlace.tempPrintId;
    if (!this.isDashboardType())
      reportResultPlace.format = format;
    if (this.isDownloadFormat(format)) {
      let viewerPresenter: ReportViewerPresenter = new ReportViewerPresenter(reportResultPlace, this.popupContainer);
      await viewerPresenter.runAndExport(format, DownloadPurpose.DOWNLOAD);
      return;
    }
    if (inNewWindow) {
      let url: string = router.resolve({
        name: RouteNames.REPORT_RESULT_ROUTE,
        query: reportResultPlace.fetchQuery()
      }).href;
      window.open(url, "_blank", "");
    } else {
      await this.showReportResult(reportResultPlace);
    }
  }

  private getDefaultParameterValues(): DTOFilledQuestionField[] {
    let defaultValues: DTOFilledQuestionField[] = ReportLauncherPresenter.createDefaultValues(this.reportMetadata);
    let questionsFromURL: DTOFilledQuestionField[] = new QuestionFieldsSerializer().questionsFromURL(this.fetchPlace().questions)?.filter(l => l.value);
    if (questionsFromURL) {
      let ids: (string | undefined)[] = questionsFromURL.map(l => l.id);
      defaultValues = defaultValues.filter(l => !ids.contains(l.id));
      defaultValues = defaultValues.concat(questionsFromURL);
    }
    ReportDimensionParametersOverrider.overrideDimensionsIfNeed(defaultValues, this.metadataContainer);
    return defaultValues;
  }

  static createDefaultValues(metadata: ReportMetadata | undefined) {
    let defaultValues: DTOFilledQuestionField[] = [];
    if (!metadata || !metadata.metadata)
      return defaultValues;
    for (let questionFieldMetadata of metadata.metadata) {
      if (questionFieldMetadata.defaultValue) {
        let filledQuestionField: DTOFilledQuestionField = {id: questionFieldMetadata.id, value: questionFieldMetadata.defaultValue};
        defaultValues.push(filledQuestionField);
      }
    }
    return defaultValues;
  }

  private getHiddenQuestions(): string[] {
    if (this.popupContainer && this.popupContainer.block?.reportInfo)
      return this.popupContainer.block?.reportInfo.hiddenParameters ?? [];
    return [];
  }

  createReportResultPlace(questionFieldsURL?: string): ReportViewerPlace {
    if (!questionFieldsURL) {
      let questionFields: DTOFilledQuestionField[] = this.getQuestionFields();
      if (this.popupContainer)
        questionFields = EmbeddedViewUtils.overrideQuestionsIfNeeded(questionFields, this.popupContainer);
      questionFieldsURL = new QuestionFieldsSerializer().toURL(questionFields);
    }
    let place: ReportViewerPlace = new ReportViewerPlace();
    place.reportId = this.fetchPlace().reportId;
    place.tempPrintId = this.fetchPlace().tempPrintId;
    place.questions = questionFieldsURL;
    if (!this.isDashboardType()) {
      place.printingOption = PrintingOption.NONE;
      place.purpose = DownloadPurpose.PREVIEW;
    }
    return place;
  }

  private async showReportResult(reportResultPlace: ReportViewerPlace) {
    if (this.popupContainer) {
      this.popupContainer.place = reportResultPlace;
      this.popupContainer.currentMode.value = "view";
    } else {
      await router.push({
        name: RouteNames.REPORT_RESULT_ROUTE,
        query: reportResultPlace.fetchQuery()
      });
    }
  }

  getQuestionFields() {
    return this.questionFieldsEditor.getQuestionFields();
  }

  getReportId(): string {
    return this.reportRef && this.reportRef.id ? this.reportRef.id : '';
  }

  override get entityType(): string {
    return this.reportRef && this.reportRef.id ? this.reportRef.id : '';
  }

  override createSearchContextInfo(fieldId: string, lineNumber?: number): FieldActionContextInfo {
    return this.fetchDispatcher().createSearchContextInfo(fieldId, lineNumber);
  }
}
